import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'shared/assets/icons';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import { defaultTagColorOption } from 'shared/constants';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useSelectedRelationShipIdsById } from 'shared/hooks/use-relationships';
import { ID, IDPrefixes } from 'shared/types/id';

import { useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction';

export type MetaTagsProps = {
  taskId?: ID;
  tagIds?: ID[];
  onChange: (relationTypeIds: ID[]) => void;
};

export const MetaTags: React.FC<MetaTagsProps> = ({
  taskId,
  tagIds,
  onChange,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const { tags, createTag, relationships } = useData();
  const { tag: taskTagIds } = useSelectedRelationShipIdsById(
    taskId,
    IDPrefixes.Task,
    relationships,
  );

  const _tagIds = tagIds ?? taskTagIds;

  const _onCreate = async (name: string) => {
    if (!name || !createTag) {
      return;
    }
    const newTag = await createTag({
      name,
      color: defaultTagColorOption.value,
    });
    onChange([..._tagIds, newTag.id]);
  };

  const tagLabels = useMemo(() => {
    const filteredTags = _tagIds?.length
      ? tags.filter((tag) => _tagIds?.includes(tag.id))
      : [];
    return filteredTags.length
      ? filteredTags.map(({ name }) => name).join(', ')
      : undefined;
  }, [_tagIds, tags]);

  const onClear = () => {
    onChange([]);
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Tag}
        placeholder={t('dialogs.task-detail.meta.tags.placeholder')}
        label={tagLabels}
        onClick={openMenu}
        onClear={tagLabels ? onClear : undefined}
      />

      {menuOpen && (
        <TagSearchMenu
          tags={tags}
          onChange={onChange}
          value={_tagIds}
          referenceElement={buttonRef}
          onCreate={createTag ? _onCreate : undefined}
          onOutside={closeMenu}
          hideCreateButton
        />
      )}
    </>
  );
};
